/**
 * EDD Generation Flow — the flagship 5-step wizard (route `/new`).
 *
 * The dead-simple journey a lab tech runs every day:
 *   1. Upload Work Order Data   — one or more Element exports (each = a work order).
 *   2. Select EDD Formats       — browse all 196 catalog formats, multi-select.
 *   3. Preview                   — the actual generated table per format × work order, with provenance
 *                                  + validation (warnings yellow, errors red).
 *   4. Confirm & Download        — generate every combo, Download All (folder per work order) or Email.
 *   5. History                   — past runs: re-download, re-generate with updated data.
 *
 * Reuses the deterministic engine end-to-end (no engine logic here). Wizard state persists across steps
 * so a user can move back and forth without losing work. The classic single-deliverable page is still
 * reachable at `/new/classic`.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Stepper } from "../components/Stepper";
import { Button } from "../components/Button";
import { Badge } from "../components/Badge";
import { Spinner } from "../components/Spinner";
import { useToast } from "../components/Toast";
import { useConfigs } from "../api/configs";
import { useGenerationJobs } from "../api/generation";
import {
  downloadFlowBundle,
  useFlowPreview,
  useRegenerateFlow,
  type FlowComboPreview,
  type FlowRunOut,
} from "../api/generateFlow";
import { errorMessage } from "../api/client";
import { UploadStep } from "../features/generate/UploadStep";
import { FormatSelectStep } from "../features/generate/FormatSelectStep";
import { PreviewStep } from "../features/generate/PreviewStep";
import { ConfirmStep } from "../features/generate/ConfirmStep";
import { track, EVENTS } from "../features/generate/telemetry";
import {
  SYNTHETIC_WORK_ORDER,
  type OutputFormat,
  type SelectedFormat,
  type WizardState,
  type WorkOrder,
} from "../features/generate/types";
import { clearWizard, loadWizard, saveWizard } from "../features/generate/wizardStorage";

const STEPS = ["Upload data", "Select formats", "Preview", "Confirm", "History"];
const OUTPUT_FORMATS: OutputFormat[] = ["DELIMITED", "TSV", "EXCEL", "XML", "ACCESS2", "ACCESS7"];
const OUTPUT_FORMAT_LABELS: Record<OutputFormat, string> = {
  DELIMITED: "CSV (comma)",
  TSV: "TSV (tab)",
  EXCEL: "Excel (.xlsx)",
  XML: "XML",
  ACCESS2: "Access (.mdb)",
  ACCESS7: "Access (2007)",
};

/** Router state passed back from the format editor when a format was built mid-wizard. */
interface ReturnFromEditor {
  createdFormatId?: string;
  createdFormatName?: string;
  returnStep?: number;
}

export function GenerateFlowPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // One-tap build→generate (Phase 33): a finished guided build hands off here as ?config_id=…. We
  // pre-select that config and jump to the format step. The org's configs resolve the display name.
  const configIdParam = searchParams.get("config_id");
  const configsQ = useConfigs();

  // Restore any in-progress wizard saved before the user left to build a new format (once, on mount).
  const [restored] = useState(loadWizard);
  // Only steps 1–2 are safe to restore: preview combos (step 3+) and the generated job (step 4/5) are
  // NOT persisted, so a restored step 3+ would render an empty/broken state. Clamp to 2.
  const [step, setStep] = useState(restored ? Math.min(restored.step, 2) : 1); // 1-based

  // ── persistent wizard state ──
  const [workOrders, setWorkOrders] = useState<WorkOrder[]>(
    restored?.workOrders ?? [SYNTHETIC_WORK_ORDER],
  );
  const [formats, setFormats] = useState<SelectedFormat[]>(restored?.formats ?? []);
  // Default = "as configured": an empty list tells the backend to export EACH template in its own
  // configured file_format (DuPont → Excel, a tab-delimited EDD → .tsv, …). Forcing a single type
  // (the old ["DELIMITED"] default) made every template come out delimited — e.g. DuPont, an EXCEL
  // template, was wrongly saved as pipe-separated SXDATA text.
  const [outputFormats, setOutputFormats] = useState<OutputFormat[]>(
    restored?.outputFormats ?? [],
  );
  const [runName, setRunName] = useState(restored?.runName ?? "");

  const [combos, setCombos] = useState<FlowComboPreview[]>([]);
  const [job, setJob] = useState<FlowRunOut | null>(null);

  const previewM = useFlowPreview();

  // Persist the live wizard so leaving to build a new format (and coming back) doesn't lose work.
  // BUT once the flow has produced a deliverable, stop persisting and wipe any saved wizard so the
  // NEXT visit to /new starts fresh at step 1 — a finished run must not be "remembered" on re-open.
  useEffect(() => {
    if (job) {
      clearWizard();
      return;
    }
    saveWizard({ step, workOrders, formats, outputFormats, runName });
  }, [step, workOrders, formats, outputFormats, runName, job]);

  // Changing the selection (work orders, formats, or output types) invalidates the prior preview
  // combos AND the already-generated job. Without this, the Confirm step keeps showing — and
  // Download keeps fetching — the FIRST run, even after the user went back and changed which formats
  // are selected. Reset both so the flow re-previews and re-generates against the CURRENT selection.
  useEffect(() => {
    setJob(null);
    setCombos((prev) => (prev.length === 0 ? prev : []));
  }, [formats, workOrders, outputFormats]);

  // Returning from the format editor: jump back to the format step and auto-select the new format.
  useEffect(() => {
    const st = location.state as ReturnFromEditor | null;
    if (!st?.createdFormatId) return;
    const id = st.createdFormatId;
    setFormats((prev) =>
      prev.some((f) => f.id === id)
        ? prev
        : [
            ...prev,
            {
              id,
              selector: { config_id: id },
              name: st.createdFormatName ?? "New format",
              kind: "config",
            },
          ],
    );
    setStep(st.returnStep ?? 2);
    // Drop the one-shot state so a refresh / back-button doesn't re-add the format.
    navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // One-tap pre-select from ?config_id (Phase 33 build→generate). Adds the config as a selected
  // format and advances to the format step (2). Config ids are valid format_ids; an absent or
  // already-selected id is ignored gracefully. The advance happens once (on first sight of the id);
  // the display name is back-filled when the org configs resolve.
  const advancedForConfigRef = useRef(false);
  useEffect(() => {
    if (!configIdParam) return;
    const match = (configsQ.data ?? []).find((c) => c.id === configIdParam);
    setFormats((prev) => {
      const existing = prev.find((f) => f.id === configIdParam);
      if (existing) {
        // Back-fill the real name/format once configs load (if we added it with the fallback).
        if (match && existing.name === "New EDD format") {
          return prev.map((f) =>
            f.id === configIdParam
              ? { ...f, name: match.name, outputFormat: match.file_format }
              : f,
          );
        }
        return prev;
      }
      return [
        ...prev,
        {
          id: configIdParam,
          selector: { config_id: configIdParam },
          name: match?.name ?? "New EDD format",
          outputFormat: match?.file_format,
          kind: "config",
        },
      ];
    });
    if (!advancedForConfigRef.current) {
      advancedForConfigRef.current = true;
      setStep(2);
    }
  }, [configIdParam, configsQ.data]);

  const state: WizardState = useMemo(
    () => ({ workOrders, formats, outputFormats, runName }),
    [workOrders, formats, outputFormats, runName],
  );

  const toggleOutput = (f: OutputFormat) =>
    setOutputFormats((prev) => (prev.includes(f) ? prev.filter((x) => x !== f) : [...prev, f]));

  // ── navigation guards ──
  const canLeaveUpload = workOrders.length > 0;
  const canLeaveFormats = formats.length > 0;

  const runPreview = async () => {
    try {
      const result = await previewM.mutateAsync({
        sources: workOrders.map((w) => w.source),
        formats: formats.map((f) => f.selector),
        output_formats: outputFormats,
        max_rows: 50,
      });
      setCombos(result);
      setStep(3);
    } catch (err) {
      track(EVENTS.error_encountered, { page: "generate_flow_preview", error_type: "preview_failed" });
      toast.error(errorMessage(err));
    }
  };

  const goNext = () => {
    if (step === 1) {
      if (!canLeaveUpload) {
        toast.error("Add at least one work order (or use the sample data).");
        return;
      }
      setStep(2);
    } else if (step === 2) {
      if (!canLeaveFormats) {
        toast.error("Select at least one EDD format.");
        return;
      }
      void runPreview();
    } else if (step === 3) {
      setStep(4);
    } else if (step === 4) {
      setStep(5);
    }
  };

  const goBack = () => setStep((s) => Math.max(1, s - 1));

  const startOver = () => {
    setWorkOrders([SYNTHETIC_WORK_ORDER]);
    setFormats([]);
    setOutputFormats([]);
    setRunName("");
    setCombos([]);
    setJob(null);
    setStep(1);
    clearWizard();
  };

  return (
    <div className="page page--wide stack">
      <div className="row row--between row--wrap">
        <div>
          <h1 style={{ marginBottom: 4 }}>Generate EDDs</h1>
          <p className="muted" style={{ margin: 0 }}>
            Upload your work order, pick your formats, preview, and deliver — in five quick steps.
          </p>
        </div>
        <div className="row" style={{ gap: "var(--space-2)" }}>
          <Link to="/generate/jobs" className="btn btn--ghost">
            History
          </Link>
          <Link to="/new/classic" className="btn btn--ghost">
            Classic flow
          </Link>
        </div>
      </div>

      <Stepper steps={STEPS} current={step} />

      <div className="card stack">
        {step === 1 && <UploadStep workOrders={workOrders} onChange={setWorkOrders} />}

        {step === 2 && (
          <div className="stack">
            <FormatSelectStep selected={formats} onChange={setFormats} />
            <div className="card card--pad-sm stack" style={{ gap: "var(--space-1)" }}>
              <strong>Output file formats</strong>
              <span className="faint">
                Leave “As configured” to export each EDD in its own configured type (e.g. DuPont as
                an Excel file). Or check one or more types to force every selected EDD into those.
              </span>
              <div className="row row--wrap" style={{ gap: "var(--space-3)" }}>
                <label className="checkbox">
                  <input
                    type="checkbox"
                    checked={outputFormats.length === 0}
                    onChange={() => setOutputFormats([])}
                  />
                  As configured (each EDD’s own type)
                </label>
                {OUTPUT_FORMATS.map((f) => (
                  <label key={f} className="checkbox">
                    <input
                      type="checkbox"
                      checked={outputFormats.includes(f)}
                      onChange={() => toggleOutput(f)}
                    />
                    {OUTPUT_FORMAT_LABELS[f]}
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}

        {step === 3 &&
          (previewM.isPending ? (
            <div className="center-pad">
              <Spinner size="lg" />
              <span>Building previews…</span>
            </div>
          ) : (
            <PreviewStep combos={combos} />
          ))}

        {step === 4 && (
          <ConfirmStep
            state={state}
            combos={combos}
            job={job}
            onGenerated={setJob}
            onStartOver={startOver}
          />
        )}

        {step === 5 && <HistoryStep lastJob={job} state={state} onStartOver={startOver} />}
      </div>

      {/* footer nav — Confirm & History manage their own primary actions */}
      <div className="row row--between">
        <Button variant="ghost" onClick={goBack} disabled={step === 1}>
          Back
        </Button>
        {step < 4 && (
          <Button variant="primary" loading={step === 2 && previewM.isPending} onClick={goNext}>
            {step === 2 ? "Preview" : "Next"}
          </Button>
        )}
        {step === 4 && job && (
          <Button variant="primary" onClick={() => setStep(5)}>
            View history
          </Button>
        )}
        {step === 5 && (
          <Button variant="primary" onClick={startOver}>
            Generate more
          </Button>
        )}
      </div>

    </div>
  );
}

// ── Step 5: History (reuses the existing generation history + adds re-generate for flow runs) ──
function HistoryStep({
  lastJob,
  state,
  onStartOver,
}: {
  lastJob: FlowRunOut | null;
  state: WizardState;
  onStartOver: () => void;
}) {
  const toast = useToast();
  const jobsQ = useGenerationJobs({ limit: 20 });
  const regen = useRegenerateFlow();
  const [busyId, setBusyId] = useState<string | null>(null);

  const flowJobs = (jobsQ.data ?? []).filter((j) => j.kind === "flow");

  const redownload = async (id: string, name: string) => {
    setBusyId(id);
    try {
      await downloadFlowBundle(id, name);
      track(EVENTS.edd_downloaded, { delivery_method: "download", source: "history" });
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  const regenerate = async (id: string) => {
    setBusyId(id);
    try {
      const result = await regen.mutateAsync({ jobId: id, body: {} });
      toast.success(
        `Re-generated ${result.completed_items} of ${result.total_items} EDDs with the latest data.`,
      );
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="stack">
      <div className="row row--between row--wrap">
        <div>
          <h2 style={{ marginBottom: 4 }}>History</h2>
          <p className="muted" style={{ margin: 0 }}>
            Your recent generation runs. Re-download a deliverable, or re-generate it with updated data.
          </p>
        </div>
        <Link to="/generate/jobs" className="btn btn--ghost">
          Full history
        </Link>
      </div>

      {lastJob && (
        <div className="banner banner--info">
          Your latest run produced {lastJob.completed_items} of {lastJob.total_items} EDDs for{" "}
          {state.workOrders.length} work order{state.workOrders.length === 1 ? "" : "s"}.
        </div>
      )}

      {jobsQ.isLoading ? (
        <Spinner size="lg" label="Loading history…" />
      ) : flowJobs.length === 0 ? (
        <div className="card card--pad-sm">
          <p className="muted" style={{ margin: 0 }}>
            No generation runs yet. <button className="link-button" onClick={onStartOver}>Start one</button>.
          </p>
        </div>
      ) : (
        <div className="stack" style={{ gap: "var(--space-2)" }}>
          {flowJobs.map((j) => (
            <div key={j.id} className="card card--pad-sm row row--between row--wrap">
              <div className="stack" style={{ gap: 2 }}>
                <div className="row" style={{ gap: "var(--space-2)", alignItems: "center" }}>
                  <Link to={`/generate/jobs/${j.id}`}>
                    <strong>{j.name || "EDD generation"}</strong>
                  </Link>
                  <Badge
                    tone={
                      j.status === "completed"
                        ? "success"
                        : j.status === "partial"
                          ? "warning"
                          : "danger"
                    }
                  >
                    {j.status}
                  </Badge>
                </div>
                <span className="faint">
                  {new Date(j.created_at).toLocaleString()} · {j.completed_items}/{j.total_items} EDDs
                </span>
              </div>
              <div className="row" style={{ gap: "var(--space-1)" }}>
                <Button
                  size="sm"
                  variant="ghost"
                  loading={busyId === j.id && regen.isPending}
                  onClick={() => void regenerate(j.id)}
                >
                  Re-generate
                </Button>
                <Button
                  size="sm"
                  variant="default"
                  disabled={!j.has_result}
                  loading={busyId === j.id && !regen.isPending}
                  onClick={() => void redownload(j.id, j.name)}
                >
                  Re-download
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
